import { Stack, Typography } from '@mui/material'
import { CardElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { StripeCardElement } from '@stripe/stripe-js/types/stripe-js/elements'
import VerticalButtonBox from 'components/buttons/VerticalButtonBox'
import StyledCardElement from 'components/form-elements/StyledCardElement'
import NotificationSys from 'components/NotificationSystem'
import { STRIPE_KEY } from 'const'
import { formatPlanPeriod, formatPlanPrice, usePlanConfig } from 'hooks/usePublicConfig'
import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { routes } from 'routes/routes'
import { TRAINEE_PLAN } from 'types'
import { createPaymentIntent } from '../../api/payment'
import { AuthFormTitle } from '../AuthFormTitle/AuthFormTitle'
import { TraineeFormType } from './types'

interface Props {
  formData: TraineeFormType
  /** Back to the access-code / purchase choice. No account exists yet at this point. */
  onBack: () => void
}

const successUrl = `${routes.successSubscription}?type=trainee`
const paymentFailedUrl = `${successUrl}&payment_failed=true`

const InnerForm = ({ formData, onBack }: Props) => {
  const stripe = useStripe()
  const elements = useElements()
  const navigate = useNavigate()
  const plan = usePlanConfig(TRAINEE_PLAN)

  const [loading, setLoading] = useState(false)

  const handleSubmitSub = async () => {
    if (!stripe || !elements) return

    try {
      setLoading(true)

      const result = await stripe.createPaymentMethod({
        type: 'card',
        card: elements.getElement(CardElement) as StripeCardElement,
        billing_details: { email: formData.email },
      })

      if (result.error) {
        NotificationSys.showWarning(
          <>
            Your payment has been declined.
            <br />
            {result.error.message}
          </>,
        )
        return
      }

      const res = await createPaymentIntent(
        formData.email,
        result.paymentMethod.id,
        'trainee',
        formData,
        TRAINEE_PLAN,
      )

      const { registrationCompleted, stripeClientSecret } = res.data

      if (registrationCompleted) {
        navigate(successUrl)
        return
      }

      if (stripeClientSecret) {
        const { error } = await stripe.confirmCardPayment(stripeClientSecret)
        if (error) {
          console.error('Payment error:', error)
          // The account already exists at this point — the backend creates it before charging the
          // card — so sending the user back to the form would retry registration with a taken
          // email. The failed-payment screen tells them to sign in and pay from My Subscription.
          navigate(paymentFailedUrl)
        } else {
          navigate(successUrl)
        }
        return
      }

      // registrationCompleted: false, no clientSecret — the bank declined the charge.
      navigate(paymentFailedUrl)
    } finally {
      setLoading(false)
    }
  }

  if (!stripe || !elements) {
    return <div>Loading...</div>
  }

  return (
    <Stack direction="column" justifyContent="space-between">
      <AuthFormTitle textColor="secondary.main">
        Individual Trainee Subscription
        {plan && (
          <>
            <br /> {formatPlanPrice(plan)} per {formatPlanPeriod(plan)}
          </>
        )}
      </AuthFormTitle>

      <Typography variant="body2" color="text.secondary" textAlign="center" mb={2}>
        Full access to MemoryCarePartner Pro Training. Your subscription renews automatically
        {plan ? ` every ${formatPlanPeriod(plan)}` : ''} until cancelled.
      </Typography>

      <StyledCardElement />

      <Typography variant="caption" color="text.secondary" mt={1}>
        Final amount may include applicable taxes and fees.
      </Typography>

      <VerticalButtonBox
        mt={4}
        onSave={handleSubmitSub}
        saveText="Submit Payment"
        loading={loading}
        disabledSave={!stripe || !elements}
        onCancel={onBack}
      />
    </Stack>
  )
}

const TraineeCardForm = (props: Props) => {
  const stripePromise = useMemo(() => loadStripe(STRIPE_KEY), [])

  return (
    <Elements stripe={stripePromise}>
      <InnerForm formData={props.formData} onBack={props.onBack} />
    </Elements>
  )
}

export default TraineeCardForm
